/**
 * Helpers for the edit page's unsaved Problem Number guard (US2138216).
 *
 * The number is the one edit-page field that needs a manual save, and a PRBT
 * left unsaved keeps the MIM working the temporary record, so leaving the page
 * or switching tabs with an unsaved change prompts first.
 */

/**
 * isUnsavedPrbtNumber reports whether the number field holds an unsaved change
 * on a temporary record: the loaded number is a PRBT and the field holds a
 * different, non-empty value.
 *
 * @param {string} [originalNumber] - The number the record was loaded or last saved with.
 * @param {string} [currentNumber] - The value currently in the number field.
 * @returns {boolean} true when leaving now would abandon the edit.
 */
export function isUnsavedPrbtNumber(originalNumber, currentNumber) {
  if (typeof originalNumber !== "string" || typeof currentNumber !== "string") {
    return false;
  }
  // Case-sensitive like ProblemForm's promotion check: numbers are stored
  // upper-case, and the two must agree on what a PRBT is.
  if (!originalNumber.startsWith("PRBT")) {
    return false;
  }
  return currentNumber !== "" && currentNumber !== originalNumber;
}

/**
 * rewriteProblemNumberInPath swaps any path segment equal to oldNumber for
 * newNumber. A destination captured before a promotion (e.g. the view link)
 * still names the PRBT, whose URL is dead once the record has moved.
 * Segments are compared decoded so an encoded segment cannot hide a match;
 * the query string and hash are left untouched.
 *
 * @param {string} path - The pending destination.
 * @param {string} oldNumber - The number the record had when the path was built.
 * @param {string} newNumber - The number the record was saved under.
 * @returns {string} The destination pointing at the saved record.
 */
export function rewriteProblemNumberInPath(path, oldNumber, newNumber) {
  if (typeof path !== "string" || !oldNumber || !newNumber || oldNumber === newNumber) {
    return path;
  }
  const suffixStart = path.search(/[?#]/);
  const pathname = suffixStart === -1 ? path : path.slice(0, suffixStart);
  const suffix = suffixStart === -1 ? "" : path.slice(suffixStart);
  const rewritten = pathname
    .split("/")
    .map((segment) => (safeDecode(segment) === oldNumber ? encodeURIComponent(newNumber) : segment))
    .join("/");
  return rewritten + suffix;
}

function safeDecode(segment) {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}
